<template>
    <div>
      <!-- Vue principale de l'exposition -->
      <div v-if="!homePage" class="radial-gallery">
        <div class="radial-container">
          <!-- Étiquette centrale affichant le titre de l'exposition -->
          <div class="central-label">{{ name }}</div>
          <!-- Positionnement radial de chaque ressource -->
          <div
            v-for="(resource, index) in resources"
            :key="resource.id"
            class="radial-item"
            :style="{
              position: 'absolute',
              left: `calc(50% + ${38 * Math.cos(((360 / resources.length) * index + rotationAngle) * Math.PI / 180)}vh - 60px)`,
              top: `calc(50% + ${38 * Math.sin(((360 / resources.length) * index + rotationAngle) * Math.PI / 180)}vh - 60px)`
            }"
          >
            <div class="item-content">
              <img
                v-if="isImage(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                alt="Resource Image"
                class="item-media"
                @click="openZoom(resource)"
              />
              <video
                v-else-if="isVideo(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                controls
                class="item-media"
              ></video>
              <div class="item-info">
                <h2 class="item-title">{{ resource.title }}</h2>
              </div>
            </div>
          </div>
        </div>
        <!-- Modal de zoom -->
        <transition name="fade">
          <div v-if="zoomedResource" class="zoom-modal" @click="closeZoom">
            <button class="close-btn" @click="closeZoom">×</button>
            <div class="zoom-content" @click.stop>
              <img 
                :src="resourceUrls[resources.indexOf(zoomedResource)]" 
                :alt="zoomedResource.title"
                class="zoomed-image"
              />
            </div>
          </div>
        </transition>
        <footer class="radial-footer">
          <span>2021 ALPA TECHNOLOGIES</span>
          <span>Mentions Légales</span>
          <span>Fattu in Corsica</span>
        </footer>
      </div>
  
      <!-- Vue d'accueil -->
      <div v-else class="home-section">
        <component
          :is="selectedTemplate"
          :homeImage="homeImage"
          @exit="exitHomePage"
        ></component>
      </div>
    </div>
  </template>
  
  <script>
  /* eslint-disable no-unused-vars */
  import { ref, onMounted, onUnmounted } from "vue";
  import axios from "axios";
  import { gsap } from "gsap";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
  import Modele_2 from "../../components/Models/Modele_2.vue";
  import Modele_3 from "../../components/Models/Modele_3.vue";
  import Modele_1 from "../../components/Models/Modele_1.vue";
  import Modele_4 from "../../components/Models/Modele_4.vue";
  import Modele_5 from "../../components/Models/Modele_5.vue";
  import Modele_6 from "../../components/Models/Modele_6.vue";
  import Modele_7 from "../../components/Models/Modele_7.vue";
  import { useRoute } from "vue-router";
  gsap.registerPlugin(ScrollTrigger);
  
  export default {
    layout: "default",
    props: [
      'homePageProps',
      'exhibitId'
    ],
    components: {
      Modele_2,
      Modele_3,
      Modele_1,
      Modele_4,
      Modele_5,
      Modele_6,
      Modele_7,
    },
    setup(props) {
      const route = useRoute();
      const id = ref(route.params.id ?? props.exhibitId);
      const resources = ref([]);
      const name = ref("");
      const userName = ref("");
      const headImage = ref([]);
      const selectedTemplate = ref("");
      const homePage = ref(props.homePageProps == false && props.exhibitId ? false : true);
      const homeImage = ref({});
      const resourceUrls = ref([]);
      const zoomedResource = ref(null);
      const rotationAngle = ref(0);
      const rotationSpeed = ref(0.1);
      const isRotating = ref(true);
      let animationFrame;

      // Variables inutilisées dans ce template (conservées dans le script)
      const isImage = (fileName) => {
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
      };
      const isVideo = (fileName) => {
        return /\.(mp4|mov|avi)$/i.test(fileName);
      };
      const leftWallResources = [];
      const centerWallResources = [];
      const rightWallResources = [];
      const roomStyle = {};
      const moveToWall = () => {};
  
      const getHeadImage = (index) => {
        let url = `${process.env.VUE_APP_BASEURL}/s3Resource/${resources.value[index].name}`;
        return url;
      };
  
      const fetchResources = async () => {
        if (resources.value.length === 0) {
          await getResources();
        }
        const urls = resources.value.map(
          (resource) =>
            `${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`
        );
        try {
          const responses = await Promise.all(urls.map((url) => axios.get(url)));
          resourceUrls.value = responses.map((response) => response.data);
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des URLs des ressources",
            error
          );
        }
      };
  
      const getUserName = async () => {
        let url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
        let response = await axios.get(url);
        userName.value = response.data.name;
      };
  
      const getHomeImage = async () => {
        try {
          let url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${id.value}`;
          let response = await axios.get(url);
          homeImage.value = response.data[0];
        } catch (error) {
          console.error(error);
        }
      };
  
      const getModel = async () => {
        try {
          let url = `${process.env.VUE_APP_BASEURL}/model/${id.value}`;
          let response = await axios.get(url);
          if (response.data.length > 0) {
            selectedTemplate.value = response.data[0].model;
            selectedTemplate.value = selectedTemplate.value
              .replace(/è/g, "e")
              .replace(/\s+/g, "_");
          }
        } catch (error) {
          console.error(error);
        }
      };
  
      const exitHomePage = () => {
        homePage.value = false;
        gsap.fromTo(
          ".radial-gallery",
          { opacity: 0 },
          { opacity: 1, duration: 1, ease: "power2.inOut" }
        );
      };

      const openZoom = (resource) => {
        zoomedResource.value = resource;
      };

      const closeZoom = () => {
        zoomedResource.value = null;
      };
  
      const getExhibitName = async () => {
        let url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
        let response = await axios.get(url);
        name.value = response.data.exhibit_name;
      };
  
      const getResources = async () => {
        let exhibitId = id.value;
        try {
          let resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
          let response = await axios.post(resourcesUrl, { exhibitId });
          let allResources = response.data;
          let galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${exhibitId}`;
          let galleryResponse = await axios.get(galleryUrl);
          let galleryResourceIds = galleryResponse.data.map((res) => res.resource_id);
          resources.value = allResources.filter((resource) =>
            galleryResourceIds.includes(resource.id)
          );
        } catch (error) {
          console.error("Error fetching resources:", error);
        }
      };
  
      const toggleRotation = () => {
        isRotating.value = !isRotating.value;
        if (isRotating.value) {
          animate();
        } else if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };

      const animate = () => {
        if (!isRotating.value) return;
        rotationAngle.value = (rotationAngle.value + rotationSpeed.value) % 360;
        animationFrame = requestAnimationFrame(animate);
      };

      onMounted(() => {
        getHomeImage();
        getResources();
        fetchResources();
        getExhibitName();
        getUserName();
        getModel();
        animate();
      });

      onUnmounted(() => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      });
  
      return {
        id,
        resources,
        name,
        userName,
        headImage,
        selectedTemplate,
        homePage,
        homeImage,
        resourceUrls,
        zoomedResource,
        openZoom,
        closeZoom,
        getHeadImage,
        exitHomePage,
        isImage,
        isVideo,
        rotationAngle,
        rotationSpeed,
        isRotating,
        toggleRotation,
      };
    },
  };
  </script>
  
  <style scoped>
  /* Style général de la galerie radiale */
  .radial-gallery {
    position: relative;
    background: radial-gradient(ellipse at center, #000, #111);
    min-height: 100vh;
    color: #f0f0f0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  /* Conteneur circulaire */
  .radial-container {
    position: relative;
    width: 70vh;
    height: 70vh;
    margin: 0 auto;
    border: 2px dashed rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    overflow: visible;
  }
  
  /* Étiquette centrale */
  .central-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 2px;
    pointer-events: none;
    text-align: center;
    width: 40%;
  }
  
  /* Chaque élément radial */
  .radial-item {
    width: 120px;
    height: 120px;
    background: rgba(20, 20, 20, 0.9);
    border-radius: 10px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    transform-origin: center center;
  }
  .radial-item:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.7);
  }
  
  /* Contenu interne de l'élément */
  .item-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5px;
    text-align: center;
  }
  .item-media {
    max-width: 100%;
    max-height: 70px;
    object-fit: cover;
    border-radius: 5px;
    margin-bottom: 5px;
  }
  .item-info {
    font-size: 0.8rem;
  }
  .item-title {
    margin: 0;
    font-weight: bold;
    font-size: 1rem;
  }
  .item-description {
    margin: 0;
    color: #aaa;
  }
  
  /* Pied de page */
  .radial-footer {
    margin-top: 20px;
    text-align: center;
    font-size: 0.9rem;
    color: #777;
  }
  
  /* Vue d'accueil */
  .home-section {
    height: 100vh;
    background: #000;
  }
  
  /* Animations de la modal */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  /* Style amélioré de la modal */
  .zoom-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    cursor: pointer;
  }
  .zoom-content {
    width: 90%;
    height: 90%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .zoomed-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
  }

  /* Style du bouton de fermeture */
  .close-btn {
    position: fixed;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    border: 2px solid #fff;
    color: #fff;
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 1001;
  }

  .close-btn:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
  </style>
  